import { createContext, useContext, useEffect, useState, useCallback } from "react";
import ro from "./locales/ro.json";
import en from "./locales/en.json";
import ru from "./locales/ru.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import es from "./locales/es.json";
import pl from "./locales/pl.json";
import tr from "./locales/tr.json";
import it from "./locales/it.json";
import pt from "./locales/pt.json";
import ua from "./locales/ua.json";
import cs from "./locales/cs.json";
import hu from "./locales/hu.json";
import nl from "./locales/nl.json";
import sv from "./locales/sv.json";
import no from "./locales/no.json";
import da from "./locales/da.json";
import fi from "./locales/fi.json";
import el from "./locales/el.json";
import bg from "./locales/bg.json";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DICT = { 
  ro, en, de, pl, tr, ru, ua, fr, es, it, pt, cs, hu, nl, sv, no, da, fi, el, bg 
};
const FALLBACK = "ro";

const I18nContext = createContext({
  lang: FALLBACK,
  setLang: () => {},
  t: (k) => k,
});

function lookup(dict, path) {
  return path.split(".").reduce((acc, k) => (acc && acc[k] != null ? acc[k] : undefined), dict);
}

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLangState] = useState(() => {
    const stored = localStorage.getItem("tf_lang");
    return DICT[stored] ? stored : FALLBACK;
  });

  // When user logs in, follow their saved language preference
  useEffect(() => {
    if (user?.language && DICT[user.language] && user.language !== lang) {
      setLangState(user.language);
      localStorage.setItem("tf_lang", user.language);
    }
  }, [user?.language]);

  const setLang = useCallback(async (code) => {
    if (!DICT[code]) return;
    setLangState(code);
    localStorage.setItem("tf_lang", code);
    if (user) {
      try { await api.patch("/users/me", { language: code }); } catch {}
    }
  }, [user]);

  const t = useCallback((key, fallback) => {
    const v = lookup(DICT[lang], key);
    if (v != null) return v;
    const fb = lookup(DICT[FALLBACK], key);
    if (fb != null) return fb;
    return fallback ?? key;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
export const useT = () => useContext(I18nContext).t;
export const useLang = () => useContext(I18nContext).lang;
